import { isDisplayableEventType } from "@arti/dashboard-events";

import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";

import { HIDDEN_EVENT_TYPES } from "../../../utils/providerProgressEventFilters";

export interface EnrichedProviderProgressEvent extends ProviderProgressEntry {
  stepNumber: number | null;
  /** True when this event is the first visible event in a new step group. */
  isFirstInStep: boolean;
}

function derivePromptCacheBreakEvent(
  event: ProviderProgressEntry,
): ProviderProgressEntry | null {
  if (event.event_type !== "prompt_cache:status") {
    return null;
  }

  const { breakReason } = event.event_data;
  if (typeof breakReason !== "string" || breakReason.length === 0) {
    return null;
  }

  return { ...event, event_type: "prompt_cache:break" };
}

export function deriveDisplayableProviderProgressEvents(
  events: readonly ProviderProgressEntry[],
): ProviderProgressEntry[] {
  const derivedEvents: ProviderProgressEntry[] = [];

  for (const event of events) {
    const promptCacheBreakEvent = derivePromptCacheBreakEvent(event);
    if (
      promptCacheBreakEvent &&
      isDisplayableEventType(promptCacheBreakEvent.event_type)
    ) {
      derivedEvents.push(promptCacheBreakEvent);
    }

    if (isDisplayableEventType(event.event_type)) {
      derivedEvents.push(event);
    }
  }

  return derivedEvents;
}

export function enrichProviderProgressEvents(
  events: ProviderProgressEntry[],
): EnrichedProviderProgressEvent[] {
  const result: EnrichedProviderProgressEvent[] = [];
  let currentStepNumber: number | null = null;
  let lastEmittedStep: number | null = null;

  for (const event of events) {
    if (event.event_type === "step:start") {
      const stepNum =
        event.event_data.stepNumber ?? event.event_data.step_number;
      if (typeof stepNum === "number") {
        currentStepNumber = stepNum;
      }
      continue;
    }

    if (HIDDEN_EVENT_TYPES.has(event.event_type)) {
      continue;
    }

    const isFirstInStep =
      currentStepNumber !== null && currentStepNumber !== lastEmittedStep;
    result.push({ ...event, isFirstInStep, stepNumber: currentStepNumber });
    lastEmittedStep = currentStepNumber;
  }

  return result;
}

export function filterVisibleProviderProgressEvents(
  events: EnrichedProviderProgressEvent[],
  visibleEventTypes?: ReadonlySet<string>,
): EnrichedProviderProgressEvent[] {
  if (!visibleEventTypes) {
    return events;
  }

  return events.filter((event) => visibleEventTypes.has(event.event_type));
}
